/* eslint-disable */
import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFilterCircleXmark, 
  faSort, 
  faSortUp, 
  faSortDown,
  faInfo
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";

import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

// ================== Funções de Estilo ==================

// Função para obter o estilo do círculo de status
const getStatusCircleStyle = (status) => {
  let background, borderColor;
  switch (status) {
    case "Red":
      background = "#FFEBEE"; // light red
      borderColor = "#E57373";
      break;
    case "Yellow":
      background = "#FFF9C4"; // light yellow
      borderColor = "#FFB74D";
      break;
    case "Green":
      background = "#C8E6C9"; // light green
      borderColor = "#81C784";
      break;
    default:
      background = "#E0E0E0";
      borderColor = "#9E9E9E";
  }
  return {
    background,
    border: `2px solid ${borderColor}`,
    borderRadius: "50%",
    width: "35px",
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  };
};

// Função para obter o estilo do círculo de nível de risco
const getRiskLevelCircleStyle = (riskLevel) => {
  let backgroundColor, borderColor;
  switch (riskLevel) {
    case "High":
      backgroundColor = "#0f0f23"; // Mesma cor da matriz High (escuro)
      borderColor = "#0f0f23";
      break;
    case "Medium":
      backgroundColor = "#2d3748"; // Mesma cor da matriz Medium (médio)
      borderColor = "#2d3748";
      break;
    case "Low":
      backgroundColor = "#718096"; // Mesma cor da matriz Low (claro)
      borderColor = "#718096";
      break;
    default:
      backgroundColor = "#9E9E9E";
      borderColor = "#9E9E9E";
  }
  return {
    backgroundColor,
    border: `2px solid ${borderColor}`,
    borderRadius: "50%",
    width: "35px",
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  };
};

// ================== Componente da Tabela de Riscos V2 ==================

const RiskTableV2 = ({ 
  risks, 
  branchOptions, 
  userOptions, 
  clientColor, 
  t,
  initialFilter = null
}) => {
  const [filters, setFilters] = useState({
    status: "",
    risk_level: initialFilter?.risk_level || "",
    probability: "",
    impact: "",
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data, config) => {
    if (!config.key) return data;
    const { key, direction } = config;
    return [...data].sort((a, b) => {
      const aVal = (a[key] || "").toString().toLowerCase();
      const bVal = (b[key] || "").toString().toLowerCase();
      if (aVal < bVal) return direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const filteredRisks = risks.filter((r) => {
    const statusOk = !filters.status || r.status === filters.status;
    const levelOk = !filters.risk_level || r.risk_level === filters.risk_level;
    const probOk = !filters.probability || r.probability === filters.probability;
    const impOk = !filters.impact || r.impact === filters.impact;
    return statusOk && levelOk && probOk && impOk;
  });

  const finalList = sortData(filteredRisks, sortConfig);

  // Estilos seguindo o padrão do RiskManagement.js
  const useStyles = makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    filterSection: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      padding: "0",
      position: "relative",
      marginBottom: "16px",
      gap: "8px",
    },
    clearFiltersWrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      paddingTop: "5.2px",
    },
    selectFormControlOverride: {
      margin: "3px 0 5px 0 !important",
      paddingTop: "1px",
      paddingBottom: "1px",
      "& > div": {
        "&:before": {
          borderBottomWidth: "0.8px !important",
        },
      },
    },
    tableHeadCell: {
      fontWeight: "bold",
      backgroundColor: "white",
      cursor: "pointer",
      padding: "12px 8px",
      fontSize: "0.875rem",
    },
    sortIcon: {
      fontSize: "14px",
      marginLeft: "6px",
      color: "#ccc",
    },
    activeSortIcon: {
      color: "#aaa",
    },

  }));

  const classes = useStyles();

  return (
    <div style={{ padding: "0 16px" }}>
      {/* Seção de filtros */}
      <div className={classes.filterSection}>
        <GridContainer spacing={0}>
          {/* Clean Filters button */}
          <GridItem xs={3} md={1}>
            <div className={classes.clearFiltersWrapper}>
              <Tooltip title={t("riskmanagement.clearAllFilters")}>
                <FontAwesomeIcon
                  icon={faFilterCircleXmark}
                  style={{
                    marginRight: "16px",
                    fontSize: "20px",
                    color: "#666",
                    cursor: "pointer",
                  }}
                  onClick={() => setFilters({
                    status: "",
                    risk_level: "",
                    probability: "",
                    impact: "",
                  })}
                />
              </Tooltip>
            </div>
          </GridItem>

          {/* Filtro por Status */}
          <GridItem xs={6} md={2}>
            <FormControl
              fullWidth
              style={{ paddingTop: "5.2px" }}
              size="medium"
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <Select
                displayEmpty
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem value="">
                  {t("riskmanagement.filters.allStatuses")}
                </MenuItem>
                <MenuItem value="Green">Green</MenuItem>
                <MenuItem value="Yellow">Yellow</MenuItem>
                <MenuItem value="Red">Red</MenuItem>
              </Select>
            </FormControl>
          </GridItem>

          {/* Filtro por Risk Level */}
          <GridItem xs={6} md={2}>
            <FormControl
              fullWidth
              style={{ paddingTop: "5.2px" }}
              size="medium"
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <Select
                displayEmpty
                value={filters.risk_level}
                onChange={(e) => handleFilterChange("risk_level", e.target.value)}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem value="">
                  {t("riskmanagement.filters.allLevels")}
                </MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </Select>
            </FormControl>
          </GridItem>

          {/* Filtro por Probability */}
          <GridItem xs={6} md={2}>
            <FormControl
              fullWidth
              style={{ paddingTop: "5.2px" }}
              size="medium"
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <Select
                displayEmpty
                value={filters.probability}
                onChange={(e) => handleFilterChange("probability", e.target.value)}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem value="">
                  {t("riskmanagement.filters.allProbabilities")}
                </MenuItem>
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
              </Select>
            </FormControl>
          </GridItem>

          {/* Filtro por Impact */}
          <GridItem xs={6} md={2}>
            <FormControl
              fullWidth
              style={{ paddingTop: "5.2px" }}
              size="medium"
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <Select
                displayEmpty
                value={filters.impact}
                onChange={(e) => handleFilterChange("impact", e.target.value)}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem value="">
                  {t("riskmanagement.filters.allImpacts")}
                </MenuItem>
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
              </Select>
            </FormControl>
          </GridItem>

          
        </GridContainer>
      </div>

             {/* Tabela de riscos */}
       <div style={{ marginBottom: "16px" }}>
        <Table style={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
                             <TableCell
                 className={classes.tableHeadCell}
                 onClick={() => handleSort("status")}
               >
                 <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                   <span>{t("riskmanagement.tableColumns.status")}</span>
                   <FontAwesomeIcon
                     icon={
                       sortConfig.key === "status"
                         ? sortConfig.direction === "ascending"
                           ? faSortUp
                           : faSortDown
                         : faSort
                     }
                     className={
                       sortConfig.key === "status"
                         ? `${classes.sortIcon} ${classes.activeSortIcon}`
                         : classes.sortIcon
                     }
                   />
                 </div>
               </TableCell>
              <TableCell
                className={classes.tableHeadCell}
                onClick={() => handleSort("risk_level")}
              >
                <div>
                  {t("riskmanagement.tableColumns.riskLevel")}
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "risk_level"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "risk_level"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
              <TableCell
                className={classes.tableHeadCell}
                onClick={() => handleSort("title")}
              >
                <div>
                  {t("riskmanagement.tableColumns.risk")}
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "title"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "title"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
              <TableCell
                className={classes.tableHeadCell}
                onClick={() => handleSort("owner")}
              >
                <div>
                  {t("riskmanagement.tableColumns.owner")}
                  <FontAwesomeIcon
                    icon={
                      sortConfig.key === "owner"
                        ? sortConfig.direction === "ascending"
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    className={
                      sortConfig.key === "owner"
                        ? `${classes.sortIcon} ${classes.activeSortIcon}`
                        : classes.sortIcon
                    }
                  />
                </div>
              </TableCell>
              <TableCell
                className={classes.tableHeadCell}
                align="center"
              >
                {t("riskmanagement.tableColumns.linkedInitiatives")}
              </TableCell>
              
            </TableRow>
          </TableHead>
        </Table>
        <PerfectScrollbar
          style={{ maxHeight: "calc(100vh - 300px)", width: "100%" }}
        >
          <Table style={{ tableLayout: "fixed", width: "100%" }}>
            <TableBody>
              {finalList.map((risk) => {
                return (
                  <TableRow key={risk.id}>
                    <TableCell>
                      <div
                        style={getStatusCircleStyle(risk.status)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div
                        style={getRiskLevelCircleStyle(risk.risk_level)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      />
                    </TableCell>
                    <TableCell
                    >
                      {risk.title}
                    </TableCell>
                    <TableCell>{risk.owner}</TableCell>
                    <TableCell align="center">
                      {risk.linkedInitiatives && risk.linkedInitiatives.length > 0 ? (
                        <div style={{ 
                          maxHeight: "80px", 
                          width: "100%",
                          position: "relative"
                        }}>
                          <PerfectScrollbar
                            style={{
                              maxHeight: "80px",
                              padding: "4px 8px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "6px",
                              border: "1px solid #e9ecef"
                            }}
                          >
                            <div style={{ fontSize: "0.8rem" }}>
                              {risk.linkedInitiatives.map((initiative, idx) => (
                                <div 
                                  key={idx} 
                                  style={{ 
                                    marginBottom: "6px",
                                    padding: "4px 6px",
                                    backgroundColor: "white",
                                    borderRadius: "4px",
                                    border: "1px solid #dee2e6",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                    wordBreak: "break-word",
                                    lineHeight: "1.3"
                                  }}
                                >
                                  {initiative.name || initiative.title}
                                </div>
                              ))}
                            </div>
                          </PerfectScrollbar>
                        </div>
                      ) : (
                        <span style={{ 
                          color: "#999", 
                          fontStyle: "italic",
                          fontSize: "0.8rem"
                        }}>
                          {t("riskmanagement.fields.none")}
                        </span>
                      )}
                    </TableCell>
                    
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </PerfectScrollbar>
      </div>
    </div>
  );
};

export default RiskTableV2;
